import { supabase } from '@cleanpro/common-ui';
import { ServiceTransformer, ServiceType } from '@cleanpro/common-ui';
import type { Service, ServiceWithChecklist } from './types';

/**
 * Fetches all service types from the 'service_types' table in the database,
 * ordered by the 'created_at' field in descending order.
 *
 * @returns {Promise<ServiceType[]>} A promise that resolves to an array of service type records.
 * @throws Will throw an error if the database query fails.
 */
export async function fetchAllServiceTypes(): Promise<ServiceType[]> {
  const { data, error } = await supabase
    .from('service_types')
    .select('*')
    .order('created_at', { ascending: false });
    
  if (error) throw error;
  
  return data as ServiceType[] || [];
}

/**
 * Fetches all services from the 'services' table in the database,
 * ordered by the 'created_at' field in descending order.
 *
 * @returns {Promise<Service[]>} A promise that resolves to an array of service records.
 * @throws Will throw an error if the database query fails.
 */
export async function fetchAllServices(): Promise<Service[]> {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .order('created_at', { ascending: false });
    
  if (error) throw error;
  if (!data) return [];
  
  // @ts-expect-error - Supabase types don't perfectly match ServiceTransformer input types
  return ServiceTransformer.transformMany(data);
}

/**
 * Adds a new service to the database
 *
 * @param serviceData - The service data without ID
 * @returns Promise<Service> - The created service
 * @throws Will throw an error if the database insert fails
 */
export async function addService(serviceData: Omit<ServiceWithChecklist, 'id'>): Promise<Service> {
  console.log('Adding service:', serviceData);
  
  // Ensure we have a valid Supabase connection
  const { error: testError } = await supabase
    .from('services')
    .select('count')
    .limit(1);
  
  if (testError) {
    console.error('Supabase connection test failed:', testError);
    throw new Error(`Database connection failed: ${testError.message}`);
  }
  
  const { data, error } = await supabase
    .from('services')
    // @ts-expect-error - Insert object types don't match exactly
    .insert({
      name: serviceData.name,
      description: serviceData.description,
      base_price: serviceData.basePrice,
      duration: serviceData.duration,
      category: serviceData.category,
      add_ons: serviceData.addOns && serviceData.addOns.length > 0 ? serviceData.addOns : [],
      checklist: serviceData.checklist || null
    })
    .select()
    .single();

  if (error) throw error;

  // @ts-expect-error - Supabase response types don't match ServiceTransformer input
  const newService = ServiceTransformer.transformOne(data);
  console.log('Service added successfully:', newService);
  return newService;
}

/**
 * Updates an existing service in the database
 *
 * @param serviceId - The ID of the service to update
 * @param serviceData - Partial service data to update
 * @returns Promise<Service> - The updated service
 * @throws Will throw an error if the database update fails
 */
export async function updateService(
  serviceId: string, 
  serviceData: Partial<Omit<ServiceWithChecklist, 'id'>>
): Promise<Service> {
  console.log('Updating service:', serviceId, serviceData);
  
  const updateData: Record<string, unknown> = {};
  if (serviceData.name !== undefined) updateData.name = serviceData.name;
  if (serviceData.description !== undefined) updateData.description = serviceData.description;
  if (serviceData.basePrice !== undefined) updateData.base_price = serviceData.basePrice;
  if (serviceData.duration !== undefined) updateData.duration = serviceData.duration;
  if (serviceData.category !== undefined) updateData.category = serviceData.category;
  if (serviceData.addOns !== undefined) updateData.add_ons = serviceData.addOns;
  if (serviceData.checklist !== undefined) updateData.checklist = serviceData.checklist;

  const { data, error } = await supabase
    .from('services')
    .update(updateData)
    .eq('id', serviceId)
    .select()
    .single();

  if (error) throw error;

  // @ts-expect-error - Supabase response types don't match ServiceTransformer input
  const updatedService = ServiceTransformer.transformOne(data);
  console.log('Service updated successfully:', updatedService);
  return updatedService;
}

/**
 * Deletes a service from the database
 *
 * @param serviceId - The ID of the service to delete
 * @returns Promise<void>
 * @throws Will throw an error if the database delete fails
 */
export async function deleteService(serviceId: string): Promise<void> {
  console.log('Deleting service:', serviceId);
  
  const { error } = await supabase
    .from('services')
    .delete()
    .eq('id', serviceId);

  if (error) throw error;

  console.log('Service deleted successfully:', serviceId);
}
